/**
 * Доверие: происшествия, центр доверия, итог дня.
 *
 * Всё, что здесь есть, обе стороны видят одинаково — это часть замысла,
 * а не упущение. Асимметрия в разделе про доверие была бы саморазоблачением.
 */

import { useMutation, useQuery, useQueryClient, type UseMutationOptions } from '@tanstack/react-query'

import { ApiError, api } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/queries'
import type { MoneyString, Role } from '@/shared/api/types'

export type IncidentSeverity = 'minor' | 'medium' | 'serious'

export const SEVERITY_LABELS: Record<IncidentSeverity, string> = {
  minor: 'Мелочь, но фиксирую',
  medium: 'Происшествие',
  serious: 'Серьёзное происшествие',
}

export const SEVERITY_HINTS: Record<IncidentSeverity, string> = {
  minor: 'Ничего страшного, но пусть будет записано',
  medium: 'Родителям стоит узнать сейчас, а не вечером',
  serious: 'Нужна реакция родителей прямо сейчас',
}

export interface Incident {
  id: number
  severity: IncidentSeverity
  title: string
  description: string
  action_taken?: string | null
  occurred_at: string
  created_by: number
  author_name: string
  child_id?: number | null
  photo_urls: string[]
  thumb_urls: string[]
  acknowledged: boolean
  acknowledged_at?: string | null
  acknowledged_by_name?: string | null
  parent_comment?: string | null
}

export interface TrustMember {
  user_id: number
  name: string
  role: Role
  geo_consent: boolean
}

export interface TrustChange {
  action: string
  label: string
  at: string
  actor_name: string
  reason?: string | null
}

export interface TrustState {
  geo_enabled: boolean
  members: TrustMember[]
  last_shift_end?: string | null
  open_shifts: number
  pending_expenses: number
  open_disputes: number
  unacknowledged_incidents: number
  unsent_timesheets: number
  needs_attention: number
  changes: TrustChange[]
}

export interface DaySummary {
  date: string
  minutes: number
  amount: MoneyString
  currency: string
  events: number
  photos: number
  expenses_count: number
  expenses_amount: MoneyString
  geo_points: number
  tasks_done: number
  tasks_left: number
  incidents: number
  shift_open: boolean
}

/* ─── Запросы ───────────────────────────────────────────────────────────── */

export function useTrust(familyId: number) {
  return useQuery<TrustState, ApiError>({
    queryKey: queryKeys.trust(familyId),
    queryFn: () => api.get<TrustState>('/trust'),
    staleTime: 30_000,
  })
}

export function useIncidents(familyId: number) {
  return useQuery<Incident[], ApiError>({
    queryKey: ['incidents', familyId],
    queryFn: () => api.get<Incident[]>('/incidents'),
    staleTime: 30_000,
  })
}

export function useDaySummary(familyId: number, date?: string) {
  return useQuery<DaySummary, ApiError>({
    queryKey: ['day-summary', familyId, date ?? 'today'],
    queryFn: () => api.get<DaySummary>('/day/summary', { query: { date } }),
    staleTime: 30_000,
  })
}

/* ─── Мутации ───────────────────────────────────────────────────────────── */

export interface IncidentInput {
  severity: IncidentSeverity
  title: string
  description: string
  action_taken?: string
  media_ids?: number[]
}

export function useReportIncident(
  familyId: number,
  options?: UseMutationOptions<Incident, ApiError, IncidentInput>,
) {
  const queryClient = useQueryClient()
  return useMutation<Incident, ApiError, IncidentInput>({
    mutationFn: (input) => api.post<Incident>('/incidents', input),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: ['incidents', familyId] })
      void queryClient.invalidateQueries({ queryKey: queryKeys.trust(familyId) })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useAcknowledgeIncident(
  familyId: number,
  options?: UseMutationOptions<Incident, ApiError, { id: number; comment?: string }>,
) {
  const queryClient = useQueryClient()
  return useMutation<Incident, ApiError, { id: number; comment?: string }>({
    mutationFn: ({ id, comment }) => api.post<Incident>(`/incidents/${id}/ack`, { comment }),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: ['incidents', familyId] })
      void queryClient.invalidateQueries({ queryKey: queryKeys.trust(familyId) })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

/* ─── Плитки событий ────────────────────────────────────────────────────── */

export interface EventTypeInput {
  emoji: string
  title: string
  requires_child?: boolean
  is_interval?: boolean
  capture_geo?: boolean
}

export function useCreateEventType(
  familyId: number,
  options?: UseMutationOptions<unknown, ApiError, EventTypeInput>,
) {
  const queryClient = useQueryClient()
  return useMutation<unknown, ApiError, EventTypeInput>({
    mutationFn: (input) => api.post('/event-types', input),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: ['event-types', familyId] })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

export function useSetEventTypeVisibility(
  familyId: number,
  options?: UseMutationOptions<unknown, ApiError, { code: string; hidden: boolean }>,
) {
  const queryClient = useQueryClient()
  return useMutation<unknown, ApiError, { code: string; hidden: boolean }>({
    mutationFn: (input) => api.patch('/event-types/visibility', input),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: ['event-types', familyId] })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}

/* ─── Места ─────────────────────────────────────────────────────────────── */

export interface Place {
  id: number
  title: string
  lat: string
  lon: string
  radius_m: number
  icon?: string | null
}

export function usePlaces(familyId: number) {
  return useQuery<Place[], ApiError>({
    queryKey: ['places', familyId],
    queryFn: () => api.get<Place[]>('/geo/places'),
    staleTime: 5 * 60_000,
  })
}

export function useArchivePlace(
  familyId: number,
  options?: UseMutationOptions<unknown, ApiError, number>,
) {
  const queryClient = useQueryClient()
  return useMutation<unknown, ApiError, number>({
    mutationFn: (id) => api.delete(`/geo/places/${id}`),
    onSuccess: (...args) => {
      void queryClient.invalidateQueries({ queryKey: ['places', familyId] })
      options?.onSuccess?.(...args)
    },
    ...options,
  })
}
